import React from 'react';
import { Chart } from 'react-chartjs-2';
import 'chart.js/auto';
import {
  Card,
  Cifra,
  FilaCifras,
  COLOR,
  eur,
  eur0,
  pct,
  num,
  sinOrden,
  TINTA_TENUE,
  REJILLA,
  leyenda,
  barra,
  useEndpoint,
} from '../ui';
import {
  BarrasH,
  CosechaCard,
  PagoMinimoCard,
  ResolucionCard,
  PrimerPagoCard,
  TramosCard,
  MarcasCard,
  CuentasCard,
} from '../PortfolioCards';

/**
 * Rendimiento de cartera: cuánto se recupera y de qué depende. Toda la historia
 * de los expedientes del cliente, no el periodo: una cosecha no se entiende
 * cortada por meses.
 */
const P = '/portfolio-performance';

export default function RendimientoSection({ params }) {
  // El periodo no aplica aquí: se piden sin él, para no recargar al cambiarlo.
  const cp = params.clients ? { clients: params.clients } : {};
  const summary = useEndpoint(`${P}/summary`, cp);
  const limpio = useEndpoint(`${P}/clean-ratio`, cp);
  const harvest = useEndpoint(`${P}/harvest`, cp);
  const minimo = useEndpoint(`${P}/minimum-payment`, cp);
  const edad = useEndpoint(`${P}/debt-age`, cp);
  const tramos = useEndpoint(`${P}/amount-bands`, cp);
  const primer = useEndpoint(`${P}/first-payment`, cp);
  const acaban = useEndpoint(`${P}/outcomes`, cp);
  const marcas = useEndpoint(`${P}/brands`, cp);
  const cuentas = useEndpoint(`${P}/accounts`, cp);

  const s = summary.data;
  const l = limpio.estado === 'ok' ? limpio.data : null;
  const deudaLimpia = l ? l.reduce((t, r) => t + r.deuda_asignada - r.deuda_retirada, 0) : 0;
  const recuperado = l ? l.reduce((t, r) => t + r.recuperado, 0) : 0;

  return (
    <div className='space-y-4'>
      <FilaCifras>
        <Cifra etiqueta='Expedientes' cargando={summary.estado === 'loading'} valor={s ? num(s.expedientes) : '—'} />
        <Cifra
          etiqueta='Deuda asignada'
          cargando={summary.estado === 'loading'}
          valor={s ? eur0(s.deuda_asignada) : '—'}
        />
        <Cifra
          etiqueta='Recuperado'
          cargando={summary.estado === 'loading'}
          valor={s ? eur0(s.recuperado) : '—'}
          pie={s ? `${pct(s.ratio_recobro_pct)} de lo asignado` : null}
        />
        <Cifra
          etiqueta='Recobro sin la cartera retirada'
          cargando={limpio.estado === 'loading'}
          valor={l && deudaLimpia > 0 ? pct(Math.round((recuperado / deudaLimpia) * 10000) / 100) : '—'}
          pie='Quitando lo que el cliente retiró como Fallido'
        />
        <Cifra
          etiqueta='Mediana al primer pago'
          cargando={summary.estado === 'loading'}
          valor={s && s.dias_mediana_al_primer_pago != null ? `${num(s.dias_mediana_al_primer_pago)} días` : '—'}
          pie={s ? `${pct(s.pct_con_pago)} de los expedientes ha pagado algo` : null}
        />
      </FilaCifras>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <CosechaCard {...harvest} />
        <RatioLimpioCard {...limpio} />
        <EdadCard {...edad} />
        <TramosCard {...tramos} />
        <PrimerPagoCard {...primer} />
        <ResolucionCard {...acaban} />
        <PagoMinimoCard {...minimo} />
        <MarcasCard {...marcas} />
        <CuentasCard {...cuentas} />
      </div>
    </div>
  );
}

const CLIENTES_EN_RATIO = 10;

function RatioLimpioCard({ estado, data }) {
  const filas = data || [];
  const top = filas.slice(0, CLIENTES_EN_RATIO);
  return (
    <Card
      titulo='¿Cuánto recuperamos de verdad, quitando la cartera que nos retiraron?'
      lectura='Por cliente, el % recuperado tal cual (gris) y sin la cartera retirada (azul). Si las dos barras se separan mucho, el bruto engaña.'
      nota='Retirada: una cosecha (cliente × mes de carga, 20 expedientes o más) con la mitad o más en Fallido o Cancelled. Se quitan de ella los retirados que no cobraron nada; lo cobrado se queda. Toda la historia.'
      estado={estado}
      vacio={!filas.length}
      filas={filas}
      csv='recobro-sin-retiradas'
      columnas={[
        { key: 'cliente', label: 'Cliente' },
        { key: 'expedientes', label: 'Expedientes', fmt: num },
        { key: 'expedientes_retirados', label: 'Retirados sin cobro', fmt: num },
        { key: 'deuda_asignada', label: 'Asignado', fmt: eur },
        { key: 'deuda_retirada', label: 'Deuda retirada', fmt: eur },
        { key: 'recuperado', label: 'Recuperado', fmt: eur },
        { key: 'ratio_bruto_pct', label: 'Recobro bruto', fmt: pct },
        { key: 'ratio_sin_retiradas_pct', label: 'Sin retiradas', fmt: pct },
      ]}
      alto={Math.max(240, top.length * 34)}
    >
      <Chart
        type='bar'
        data={{
          labels: top.map(f => f.cliente),
          datasets: [
            barra(
              'Bruto',
              top.map(f => f.ratio_bruto_pct),
              '#898781'
            ),
            barra(
              'Sin retiradas',
              top.map(f => f.ratio_sin_retiradas_pct),
              COLOR.s1
            ),
          ],
        }}
        options={{
          indexAxis: 'y',
          maintainAspectRatio: false,
          interaction: { mode: 'index', axis: 'y', intersect: false },
          plugins: {
            legend: leyenda,
            tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${pct(ctx.parsed.x)}` } },
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: { color: REJILLA },
              border: { display: false },
              ticks: { color: TINTA_TENUE },
            },
            y: { grid: { display: false }, ticks: { color: TINTA_TENUE } },
          },
        }}
      />
    </Card>
  );
}

function EdadCard({ estado, data }) {
  const filas = (data || []).map(f => ({ ...f, antiguedad_al_entrar: sinOrden(f.antiguedad_al_entrar) }));
  return (
    <Card
      titulo='¿Se recupera peor la deuda que nos llega más vieja?'
      lectura='El % recuperado según los días que tenía la deuda cuando nos la pasaron. Si cae hacia la derecha, cuanto antes nos la den, mejor.'
      nota='Antigüedad: de la fecha de la primera factura a su carga. Una dispersión de más de 300 días en un cliente es un vaciado de cartera, no un flujo. Toda la historia.'
      estado={estado}
      vacio={!filas.length}
      filas={filas}
      csv='recobro-por-antiguedad'
      columnas={[
        { key: 'antiguedad_al_entrar', label: 'Antigüedad al entrar' },
        { key: 'expedientes', label: 'Expedientes', fmt: num },
        { key: 'deuda_asignada', label: 'Asignado', fmt: eur },
        { key: 'recuperado', label: 'Recuperado', fmt: eur },
        { key: 'ratio_recobro_pct', label: 'Recobro', fmt: pct },
        { key: 'pct_con_pago', label: 'Con pago', fmt: pct },
      ]}
    >
      <BarrasH
        titulo='Recobro'
        etiquetas={filas.map(f => f.antiguedad_al_entrar)}
        valores={filas.map(f => f.ratio_recobro_pct)}
        fmt={pct}
      />
    </Card>
  );
}
